import React from 'react';
import { Helmet } from 'react-helmet';
import { FaHeartbeat, FaBrain, FaBone, FaBaby, FaEye, FaTooth, FaLungs, FaMicroscope, FaArrowRight, FaStethoscope, FaUserMd, FaHospital, FaAmbulance, FaPhoneAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Base from './Base';

function Services() {
  const allServices = [
    { id: "cardiology", icon: <FaHeartbeat />, title: "Cardiology", desc: "Our cardiology department offers comprehensive care for heart-related conditions." },
    { id: "neurology", icon: <FaBrain />, title: "Neurology", desc: "Advanced treatment and diagnostics for disorders of the nervous system." },
    { id: "orthopedics", icon: <FaBone />, title: "Orthopedics", desc: "Specialized care for musculoskeletal injuries and degenerative conditions." },
    { id: "pediatrics", icon: <FaBaby />, title: "Pediatrics", desc: "Expert medical care for infants, children, and adolescents." },
    { id: "ophthalmology", icon: <FaEye />, title: "Ophthalmology", desc: "Specialized eye care from routine exams to advanced surgical procedures for vision health." },
    { id: "dental", icon: <FaTooth />, title: "Dental Care", desc: "Modern dentistry including preventive care, cosmetic treatments, and complex oral surgery." },
    { id: "pulmonology", icon: <FaLungs />, title: "Pulmonology", desc: "Specialized treatment for respiratory conditions including asthma, COPD, and sleep apnea." },
    { id: "diagnostics", icon: <FaMicroscope />, title: "Diagnostics", desc: "State-of-the-art laboratory and imaging services for accurate and timely medical diagnoses." },
    { id: "urology", icon: <FaStethoscope />, title: "Urology", desc: "Advanced care for urinary tract conditions and male reproductive health using modern techniques." },
    { id: "dermatology", icon: <FaUserMd />, title: "Dermatology", desc: "Expert care for skin, hair, and nail health, including both medical and aesthetic treatments." },
    { id: "emergency", icon: <FaAmbulance />, title: "Emergency Care", desc: "24/7 high-priority medical assistance for critical situations with board-certified trauma experts." },
    { id: "internist", icon: <FaHospital />, title: "General Medicine", desc: "Comprehensive primary care and internal medicine focus on overall wellness and disease prevention." },
  ];

  return (
    <Base>
      <div className="dark:bg-dark-800 min-h-screen transition-colors duration-200">
        <Helmet>
          <title>Medical Services | Aman Medical Complex Gilgit</title>
          <meta name="description" content="Comprehensive medical services including Cardiology, Neurology, Pediatrics, Orthopedics, Emergency Care, and more at Aman Medical Complex Gilgit." />
          <meta name="keywords" content="medical services, cardiology, neurology, pediatrics, orthopedics, emergency care, hospital services gilgit" />
          <meta property="og:title" content="Medical Services | Aman Medical Complex Gilgit" />
          <meta property="og:description" content="Wide range of medical services to ensure our patients receive the best care possible." />
        </Helmet>

        {/* Page Title Section */}
        <section className="relative py-24 overflow-hidden bg-gradient-to-br from-primary-50 to-white dark:from-dark-800 dark:to-dark-900">
          <div className="absolute inset-0 z-0 opacity-5">
            <div className="absolute top-10 left-10 w-72 h-72 bg-primary-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-primary-300 rounded-full blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <div className="inline-block mb-4">
              <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs md:text-sm">OUR SERVICES</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-navy-500 dark:text-white mb-6 tracking-tight">Our Medical Services</h1>
            <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              We provide wide range of medical services to ensure that our patients receive the best care possible.
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {allServices.map((service, i) => (
                <div
                  key={service.id}
                  className="group bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-soft hover:shadow-card-hover transition-all duration-300 border border-gray-100 dark:border-dark-700 card-hover-lift"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  {/* Icon */}
                  <div className="w-16 h-16 bg-primary-50 dark:bg-primary-900/20 rounded-2xl flex items-center justify-center text-primary-500 text-3xl mb-6 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300 icon-bounce">
                    {service.icon}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-black text-navy-500 dark:text-white mb-3 leading-tight group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6 text-sm">
                    {service.desc}
                  </p>

                  {/* Read More Link */}
                  <Link
                    to={`/appointment`}
                    className="inline-flex items-center gap-2 text-primary-500 font-bold text-sm hover:gap-3 transition-all group/link"
                  >
                    Read More <FaArrowRight size={12} className="group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-24 bg-gradient-to-br from-navy-500 to-navy-700 dark:from-dark-900 dark:to-dark-800 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-300 rounded-full blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <div className="lg:w-1/2">
                <span className="text-primary-400 font-bold uppercase tracking-[0.2em] text-xs mb-4 inline-block">WHY CHOOSE US</span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">Why Choose Our Medical Services?</h2>
                <p className="text-white/80 text-lg mb-10 leading-relaxed">
                  Aman Medical Complex Gilgit provides state-of-the-art facilities and a compassionate approach to patient care, led by a team of highly-qualified medical specialists.
                </p>
                <ul className="space-y-5">
                  {[
                    "Advanced Diagnostic Technology",
                    "Board-Certified Medical Specialists",
                    "24/7 Emergency & Critical Care",
                    "Personalized Treatment Plans"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-4 text-white text-lg font-semibold">
                      <span className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm flex-shrink-0">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:w-1/2 grid grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20">
                  <h4 className="text-5xl font-black text-primary-400 mb-2">25k+</h4>
                  <p className="text-white/80 font-semibold">Happy Patients</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20 mt-10">
                  <h4 className="text-5xl font-black text-primary-400 mb-2">150+</h4>
                  <p className="text-white/80 font-semibold">Specialist Doctors</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20 -mt-10">
                  <h4 className="text-5xl font-black text-primary-400 mb-2">24/7</h4>
                  <p className="text-white/80 font-semibold">High Priority Care</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20">
                  <h4 className="text-5xl font-black text-primary-400 mb-2">12+</h4>
                  <p className="text-white/80 font-semibold">Departments</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-4">
            <div className="bg-gradient-to-r from-primary-500 to-primary-700 rounded-3xl p-12 md:p-16 flex flex-col lg:flex-row items-center justify-between gap-10 relative overflow-hidden shadow-strong">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="lg:w-2/3 text-white text-center lg:text-left relative z-10">
                <h2 className="text-3xl md:text-4xl font-black mb-6 leading-tight">Ready to Take Care of Your Health?</h2>
                <p className="text-xl opacity-90 mb-8 font-medium max-w-2xl">
                  Schedule an appointment today with our specialized doctors and get the best medical care Gilgit has to offer.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-6 text-2xl font-black">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-2xl shadow-xl">
                    <FaPhoneAlt />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm opacity-70 uppercase tracking-widest mb-1 font-semibold">Emergency Call</span>
                    <span className="text-white">+92-345 5558297</span>
                  </div>
                </div>
              </div>
              <div className="lg:w-1/3 text-center relative z-10">
                <Link to="/appointment" className="group inline-flex items-center justify-center gap-3 bg-white text-primary-600 hover:bg-gray-50 px-10 py-5 rounded-full font-black text-lg transition-all duration-300 hover:scale-105 active:scale-95 shadow-2xl">
                  Book Appointment <FaArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Base>
  );
}

export default Services;
